import { computed } from 'vue'
import { useRoute, useRouter, type RouteLocationNormalized, type RouteLocationPathRaw } from 'vue-router'
import { useBackNavigation } from '@/composables/useBackNavigation'
import type { Content } from '@/services/content.service'

export type ContentWorkflowStage = 'write' | 'preview' | 'settings' | 'review' | 'results'
export type ContentWorkflowChannel = 'social' | 'blog' | 'email'
/**
 * draft: writing and deciding. out: handed to delivery (scheduled, sending, sent, failed) — read-only.
 * live: a published Blog article; it is edited straight away and Review publishes the change.
 */
export type ContentWorkflowPhase = 'draft' | 'out' | 'live'

const stages: readonly ContentWorkflowStage[] = ['write', 'preview', 'settings', 'review', 'results']
const channels: readonly string[] = ['social', 'blog', 'email']
const phaseStages: Record<ContentWorkflowPhase, readonly ContentWorkflowStage[]> = {
  draft: stages,
  out: ['preview', 'review', 'results'],
  live: ['write', 'review', 'results'],
}
// Blog (Jens, 2026-10-21): Preview is a toggle on the Write step, not a step
// of its own. A new post goes Write · Settings · Review · Results; a live
// post goes Write · Review · Results, with no separate edit mode.
const blogPhaseStages: Record<ContentWorkflowPhase, readonly ContentWorkflowStage[]> = {
  draft: ['write', 'settings', 'review', 'results'],
  out: ['preview', 'review', 'results'],
  live: ['write', 'review', 'results'],
}
const outStates = ['scheduled', 'publishing', 'published', 'partially_published', 'pending_changes', 'failed']
type WorkflowRoute = Pick<RouteLocationNormalized, 'path' | 'params' | 'query' | 'hash'>

/** Steps shown in the bar for a phase, in order. Addresses never change; only the visible steps and their names do. */
export function contentWorkflowPhaseStages(phase: ContentWorkflowPhase, channel?: ContentWorkflowChannel | string): readonly ContentWorkflowStage[] {
  return (channel === 'blog' ? blogPhaseStages : phaseStages)[phase]
}

/** Label key for a step. After delivery, Preview reads Content and Review reads Summary. */
export function contentWorkflowStageLabelKey(stage: ContentWorkflowStage, phase: ContentWorkflowPhase = 'draft'): string {
  if (phase === 'out' && stage === 'preview') return 'contentWorkflow.content'
  if (phase === 'out' && stage === 'review') return 'contentWorkflow.summary'
  return `contentWorkflow.${stage}`
}

/** The content has been handed to delivery. Nothing about it is editable any more. */
export function contentIsOut(content: Pick<Content, 'state' | 'placements'> | null | undefined): boolean {
  if (!content) return false
  if (outStates.includes(content.state)) return true
  return Boolean(content.placements?.some(placement => placement.state !== 'draft' && placement.state !== 'cancelled'))
}

/** Where an address lands in a phase that does not show that step. */
export function contentWorkflowPhaseStage(phase: ContentWorkflowPhase, stage: ContentWorkflowStage, channel?: ContentWorkflowChannel | string): ContentWorkflowStage {
  if (phase === 'out') {
    // A sent, scheduled or published item opens on Results; its Settings are frozen into the Summary.
    if (stage === 'write') return 'results'
    if (stage === 'settings') return 'review'
    return stage
  }
  if (phase === 'live') {
    // A live article's Settings stay as decided; Review publishes the change.
    if (stage === 'settings') return 'review'
    if (stage === 'preview') return 'write'
    return stage
  }
  // Blog has no Preview step: an old /preview link opens Write.
  if (channel === 'blog' && stage === 'preview') return 'write'
  return stage
}

/** One route record keeps the editor and its unsaved work mounted across stages. */
export function contentWorkflowEditorPath(channel: ContentWorkflowChannel): string {
  return `/:workspaceId/${channel}/:contentId/:stage(preview|settings|review|results)?`
}

export function contentWorkflowStageFromRoute(route: WorkflowRoute): ContentWorkflowStage {
  // Old links use internal stage names. Invalid legacy values open Write.
  if (Object.prototype.hasOwnProperty.call(route.query, 'stage')) {
    const value = route.query.stage
    if (value === 'publish') return 'settings'
    return typeof value === 'string' && stages.includes(value as ContentWorkflowStage)
      ? value as ContentWorkflowStage
      : 'write'
  }
  const value = route.params.stage
  return typeof value === 'string' && stages.includes(value as ContentWorkflowStage) ? value as ContentWorkflowStage : 'write'
}

/** Change only the stage or new draft ID; retain the rest of the address. */
export function contentWorkflowLocation(
  route: WorkflowRoute,
  stage: ContentWorkflowStage = contentWorkflowStageFromRoute(route),
  contentId: string | number = String(route.params.contentId),
): RouteLocationPathRaw {
  const channel = route.path.split('/')[2]
  const base = `/${encodeURIComponent(String(route.params.workspaceId))}/${channel}/${encodeURIComponent(String(contentId))}`
  const suffix = stage === 'write' ? '' : `/${stage}`
  const query = { ...route.query }
  delete query.stage
  return { path: `${base}${suffix}`, query, hash: route.hash }
}

/** Redirect old query links once, without adding an extra Back entry. */
export function canonicalContentWorkflowLocation(route: RouteLocationNormalized): RouteLocationPathRaw | undefined {
  if (!channels.includes(String(route.name)) || !Object.prototype.hasOwnProperty.call(route.query, 'stage')) return
  return { ...contentWorkflowLocation(route), replace: true }
}

/** Keep each editor mounted while its work areas change; preserve other link options. */
export function useContentWorkflowStage() {
  const route = useRoute()
  const router = useRouter()
  const { keepReturnTo } = useBackNavigation()
  const stage = computed(() => contentWorkflowStageFromRoute(route))

  async function setStage(next: ContentWorkflowStage) {
    if (stage.value === next && !Object.prototype.hasOwnProperty.call(route.query, 'stage')) return
    await router.replace(keepReturnTo(contentWorkflowLocation(route, next)))
  }

  return { stage, setStage }
}
